import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useBranch } from '@/context/BranchContext';
import { useSalesSettings } from '@/hooks/useSalesSettings';
import { useProfits } from '@/hooks/useProfits';
import { formatMoney } from '@/lib/format';
import { friendlyError } from '@/lib/errors';
import {
  profitChange,
  type ProfitPeriodMode,
  type ProfitSortKey,
} from '@/lib/profits';
import ProfitRing from './components/ProfitRing';
import ProfitFilters, { type CashierOption } from './components/ProfitFilters';
import ProfitRankingTable from './components/ProfitRankingTable';

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayStrIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function ProfitsPage() {
  const { isAdmin } = useAuth();
  const { branches, activeBranchId } = useBranch();
  const { settings } = useSalesSettings();

  const [mode, setMode] = useState<ProfitPeriodMode>('month');
  const [customStart, setCustomStart] = useState(isoDaysAgo(6));
  const [customEnd, setCustomEnd] = useState(todayStrIso());
  const [branchId, setBranchId] = useState<string | null>(activeBranchId);
  const [cashierId, setCashierId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [productQuery, setProductQuery] = useState('');
  const [sortKey, setSortKey] = useState<ProfitSortKey>('profit');
  const [cashiers, setCashiers] = useState<CashierOption[]>([]);

  const currencySymbol = settings?.currency_symbol ?? 'RD$';

  useEffect(() => {
    setBranchId(activeBranchId);
  }, [activeBranchId]);

  useEffect(() => {
    if (!isAdmin) return;
    let active = true;
    supabase
      .from('profiles')
      .select('id, first_name, last_name, username, email, role')
      .order('first_name')
      .then(({ data }) => {
        if (!active || !data) return;
        setCashiers(
          data.map((p: Record<string, unknown>) => {
            const name =
              [p.first_name, p.last_name].filter(Boolean).join(' ') ||
              (p.username as string) ||
              (p.email as string) ||
              'Usuario';
            const role = ['super_admin', 'administrador'].includes(String(p.role))
              ? 'Administrador'
              : 'Cajero';
            return { id: String(p.id), name: String(name), role };
          })
        );
      });
    return () => {
      active = false;
    };
  }, [isAdmin]);

  const { rows, summary, prevSummary, categories, loading, error, reload } = useProfits({
    mode,
    customStart,
    customEnd,
    branchId,
    cashierId,
    productQuery,
    categoryId,
  });

  const change = useMemo(
    () => (prevSummary ? profitChange(summary.profit, prevSummary.profit) : null),
    [summary.profit, prevSummary]
  );

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      if (sortKey === 'quantity') return b.quantity - a.quantity;
      if (sortKey === 'revenue') return b.revenue - a.revenue;
      if (sortKey === 'margin') return b.margin - a.margin;
      return b.profit - a.profit;
    });
    return copy;
  }, [rows, sortKey]);

  const tiles = [
    { label: 'Ventas totales', value: formatMoney(summary.revenue, currencySymbol), icon: 'ri-money-dollar-circle-line', tone: 'bg-primary-100 text-primary-700' },
    { label: 'Costo de mercancía', value: formatMoney(summary.cost, currencySymbol), icon: 'ri-shopping-bag-3-line', tone: 'bg-background-200 text-foreground-700' },
    { label: 'Ganancia neta', value: formatMoney(summary.profit, currencySymbol), icon: 'ri-line-chart-line', tone: 'bg-accent-100 text-accent-700' },
    { label: 'Margen promedio', value: `${summary.margin.toFixed(1)}%`, icon: 'ri-percent-line', tone: 'bg-secondary-100 text-secondary-900' },
    { label: 'Productos vendidos', value: String(summary.quantity), icon: 'ri-stack-line', tone: 'bg-secondary-100 text-secondary-900' },
    { label: 'Devoluciones', value: formatMoney(summary.refunds, currencySymbol), icon: 'ri-refund-2-line', tone: 'bg-background-200 text-foreground-700' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Ganancias</h1>
          <p className="mt-1 text-sm text-foreground-600">
            Análisis de ganancias reales por producto, sucursal y período.
          </p>
        </div>
        <button
          onClick={reload}
          className="inline-flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer self-start"
        >
          <i className="ri-refresh-line" /> Actualizar
        </button>
      </div>

      <ProfitFilters
        mode={mode}
        customStart={customStart}
        customEnd={customEnd}
        onMode={setMode}
        onCustomStart={setCustomStart}
        onCustomEnd={setCustomEnd}
        isAdmin={isAdmin}
        branchId={branchId}
        onBranch={setBranchId}
        branches={branches}
        cashierId={cashierId}
        onCashier={setCashierId}
        cashiers={cashiers}
        categoryId={categoryId}
        onCategory={setCategoryId}
        categories={categories}
        productQuery={productQuery}
        onProductQuery={setProductQuery}
      />

      {error ? (
        <div className="rounded-lg border border-accent-200 bg-accent-50 p-6 text-center">
          <i className="ri-error-warning-line text-4xl text-accent-700" />
          <p className="mt-3 text-foreground-800">{friendlyError(error, 'No se pudieron calcular las ganancias.')}</p>
          <button
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
          >
            <i className="ri-refresh-line" /> Reintentar
          </button>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : (
        <>
          {/* Indicador circular + comparación */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 items-center">
              <div className="flex justify-center">
                <ProfitRing
                  profit={summary.profit}
                  revenue={summary.revenue}
                  cost={summary.cost}
                  margin={summary.margin}
                  currencySymbol={currencySymbol}
                />
              </div>

              <div className="space-y-4">
                <div>
                  {change == null ? (
                    <p className="text-sm text-foreground-500">
                      Sin datos del período anterior para comparar.
                    </p>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${
                        change >= 0 ? 'bg-accent-100 text-accent-900' : 'bg-primary-100 text-primary-800'
                      }`}
                    >
                      <i className={change >= 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
                      {change >= 0 ? '+' : ''}
                      {change.toFixed(1)}% respecto al período anterior
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {tiles.map((t) => (
                    <div key={t.label} className="rounded-lg border border-background-200 bg-background-100/60 p-3.5">
                      <div className="flex items-center gap-2 text-foreground-600">
                        <span className={`w-8 h-8 rounded-md flex items-center justify-center ${t.tone}`}>
                          <i className={`${t.icon} text-base`} />
                        </span>
                        <span className="text-xs font-medium">{t.label}</span>
                      </div>
                      <p className="mt-2 font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
                        {t.value}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <ProfitRankingTable
            rows={sortedRows}
            sortKey={sortKey}
            onSort={setSortKey}
            currencySymbol={currencySymbol}
          />
        </>
      )}
    </div>
  );
}